import { useCallback, useEffect, useRef, useState } from "react";
import { //BrowserRouter as Router,
          Routes,
          Route,
          //Link
        } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import './App.css';
// import { useRoutes } from "react-router-dom";
import { RegisterForm } from "./Components/RegisterForm";
import LoginForm from "./Components/LoginForm";
import Clients from "./Components/Clients";
import Customers from "./Components/Customers";
import Facilities from "./Components/Facilities";
import Pods from "./Components/Pods";
import Users from "./Components/Users";
import Protocols from "./Components/Protocols";
import LandingPage from "./Components/LandingPage";
import UserProfile from "./Components/UserProfile";
import UserView from "./Components/UserView";
import Module from "./Components/Module";
import NavbarTop from "./Components/Navbar";
import ProtocolUsage from "./Components/ProtocolUsage";
import ResetPasswordForm from "./Components/ResetPasswordForm";
import UserPasswordReset from "./Components/UserPasswordReset";
import { clearAuthSession, fetchCurrentUserProfile, getLastRefreshAt, initializeAuth, isAuthenticated, refreshAccessToken, validateSessionHeartbeat } from "./utils/auth";

const INACTIVITY_LIMIT_MS = 20 * 60 * 1000;
const WARNING_COUNTDOWN_SECONDS = 60;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
const HEARTBEAT_REFRESH_MAX_AGE_MS = 5 * 60 * 1000;

function NoMatch() {
  return (
    <div style={{ padding: 20 }}>
      <h2>404: Page Not Found</h2>
      <p><font color="white">There is no page at the requested url</font></p>
    </div>
  );
}

function UnAuthorized() {
  return (
    <div style={{ padding: 20 }}>
      <h2>401: Unauthorized</h2>
      <p><font color="white">You are not authorized to view the page at the requested url</font></p>
    </div>
  );
}

function ComingSoon() {
  if (!isAuthenticated()) { return UnAuthorized() }
  else {
    return (
      <div style={{ padding: 20 }}>
        <h2>Coming Soon!</h2>
        <p><font color="white">We are continually adding new and amazing features - this page will be available soon</font></p>
      </div>
    );
  }
}

function App() {
  const navigate = useNavigate();
  const [authStatus, setAuthStatus] = useState(isAuthenticated());
  const [showIdleDialog, setShowIdleDialog] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(WARNING_COUNTDOWN_SECONDS);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);

  const idleWarningTimeoutRef = useRef(null);
  const idleSignOutTimeoutRef = useRef(null);
  const idleCountdownIntervalRef = useRef(null);
  const countdownValueRef = useRef(WARNING_COUNTDOWN_SECONDS);
  const forceSignOutRef = useRef(null);

  const clearIdleTimers = useCallback(() => {
    if (idleWarningTimeoutRef.current) {
      window.clearTimeout(idleWarningTimeoutRef.current);
      idleWarningTimeoutRef.current = null;
    }

    if (idleSignOutTimeoutRef.current) {
      window.clearTimeout(idleSignOutTimeoutRef.current);
      idleSignOutTimeoutRef.current = null;
    }

    if (idleCountdownIntervalRef.current) {
      window.clearInterval(idleCountdownIntervalRef.current);
      idleCountdownIntervalRef.current = null;
    }
  }, []);

  const forceSignOut = useCallback(() => {
    clearIdleTimers();
    setShowIdleDialog(false);
    clearAuthSession();
    setAuthStatus(false);
    navigate('/login', { replace: true });
  }, [clearIdleTimers, navigate]);

  useEffect(() => {
    forceSignOutRef.current = forceSignOut;
  }, [forceSignOut]);

  const startIdleWarning = useCallback(() => {
    if (!isAuthenticated()) {
      return;
    }

    clearIdleTimers();
    setShowIdleDialog(true);
    countdownValueRef.current = WARNING_COUNTDOWN_SECONDS;
    setIdleCountdown(WARNING_COUNTDOWN_SECONDS);

    idleCountdownIntervalRef.current = window.setInterval(() => {
      countdownValueRef.current -= 1;
      setIdleCountdown(Math.max(countdownValueRef.current, 0));

      if (countdownValueRef.current <= 0) {
        forceSignOut();
      }
    }, 1000);

    idleSignOutTimeoutRef.current = window.setTimeout(() => {
      forceSignOut();
    }, WARNING_COUNTDOWN_SECONDS * 1000);
  }, [clearIdleTimers, forceSignOut]);

  const scheduleIdleWarning = useCallback(() => {
    clearIdleTimers();
    if (!isAuthenticated()) {
      return;
    }

    idleWarningTimeoutRef.current = window.setTimeout(() => {
      startIdleWarning();
    }, INACTIVITY_LIMIT_MS);
  }, [clearIdleTimers, startIdleWarning]);

  useEffect(() => {
    initializeAuth();
    setAuthStatus(isAuthenticated());

    const authSyncTimer = window.setInterval(() => {
      setAuthStatus(isAuthenticated());
    }, 5000);

    const syncOnFocus = () => setAuthStatus(isAuthenticated());
    window.addEventListener('focus', syncOnFocus);

    const refreshTimer = window.setInterval(async () => {
      if (!isAuthenticated()) {
        return;
      }

      const lastRefreshAt = getLastRefreshAt();
      const shouldRunHeartbeat = !lastRefreshAt || (Date.now() - lastRefreshAt) >= HEARTBEAT_REFRESH_MAX_AGE_MS;
      if (!shouldRunHeartbeat) {
        return;
      }

      const stillValid = await validateSessionHeartbeat();
      if (!stillValid) {
        forceSignOutRef.current?.();
      }
    }, 60 * 1000);

    return () => {
      window.clearInterval(refreshTimer);
      window.clearInterval(authSyncTimer);
      window.removeEventListener('focus', syncOnFocus);
    };
  }, []);

  useEffect(() => {
    if (!authStatus) {
      setShowIdleDialog(false);
      clearIdleTimers();
      return;
    }

    scheduleIdleWarning();
  }, [authStatus, clearIdleTimers, scheduleIdleWarning]);

  useEffect(() => {
    const handleActivity = () => {
      if (!isAuthenticated() || showIdleDialog) {
        return;
      }

      scheduleIdleWarning();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [scheduleIdleWarning, showIdleDialog]);

  useEffect(() => {
    return () => {
      clearIdleTimers();
    };
  }, [clearIdleTimers]);

  const handleStaySignedIn = async () => {
    setIsRefreshingSession(true);

    try {
      await refreshAccessToken();
      await fetchCurrentUserProfile();
      setShowIdleDialog(false);
      setIdleCountdown(WARNING_COUNTDOWN_SECONDS);
      countdownValueRef.current = WARNING_COUNTDOWN_SECONDS;
      setAuthStatus(isAuthenticated());
      scheduleIdleWarning();
    } catch (error) {
      forceSignOut();
    } finally {
      setIsRefreshingSession(false);
    }
  };

  return (
    <>
      <NavbarTop />
          <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/facilities" element={<Facilities />} />
            <Route path="/pods" element={<Pods />} />
            <Route path="/users" element={<Users />} />
            <Route path="/protocols" element={<Protocols />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/resetpassword" element={<ResetPasswordForm />} />
            <Route path="/users/:id/reset-password" element={<UserPasswordReset />} />
            <Route path="/userprofile" element={<UserProfile />} />
            <Route path="/users" element={<UserView />} />
            <Route path="/modules/:id" element={<Module />} />
            <Route path="/unauthorized" element={<UnAuthorized />} />
            <Route path="/comingsoon" element={<ComingSoon />} />
            <Route path="/protocolusage" element={<ProtocolUsage />} />
            <Route path="*" element={<NoMatch />} />
          </Routes>
          </div>

          {showIdleDialog && (
            <div className='idle-session-overlay'>
              <div className='idle-session-modal'>
                <h3>Session Expiring</h3>
                <p>Your session is about to end due to inactivity.</p>
                <p className='idle-session-countdown'>Signing out in {idleCountdown}s</p>
                <div className='idle-session-actions'>
                  <button type='button' className='btn btn-cancel' onClick={forceSignOut} disabled={isRefreshingSession}>Sign Out</button>
                  <button type='button' className='btn' onClick={handleStaySignedIn} disabled={isRefreshingSession}>
                    {isRefreshingSession ? 'Refreshing...' : 'Stay Signed In'}
                  </button>
                </div>
              </div>
            </div>
          )}
    </>
  );
}

export default App;
